// libraries
import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  ErrorList,
  getFontCss,
  styleConstants,
  TextInputField,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import CodeVerification from 'src/components/CodeVerification/CodeVerification'
import PasswordInputField from 'src/components/PasswordInputField/PasswordInputField'
import { useBackgroundDispatch, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { postCognitoResetPasswordConfirm } from 'src/redux/actions/auth.actions'
import {
  selectResetPasswordErrorDetailMapFromState,
  selectResetPasswordTopLevelErrorFromState,
  selectResettingPasswordFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  stringHasSpace,
  stringIsEmpty,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface ForgotPasswordFormProps {
  email: string
}

export type ForgotPasswordFormFlatFields = {
  emailCode: string
  newPassword: string
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()

  const topLevelError = useSelector(selectResetPasswordTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectResetPasswordErrorDetailMapFromState)
  const loading = useSelector(selectResettingPasswordFromState)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const passedFlatFieldsValidation = flatFieldsSetErrors()

    if (passedFlatFieldsValidation) {
      dispatch(
        postCognitoResetPasswordConfirm({
          code: flatFields.emailCode,
          email: props.email,
          newPassword: flatFields.newPassword,
        }),
      )
    }
  }

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<ForgotPasswordFormFlatFields>(
    {
      emailCode: '',
      newPassword: '',
    },
    () => ({
      emailCode: [stringIsEmpty(), stringHasSpace],
      newPassword: [stringIsEmpty(), stringHasSpace],
    }),
    errorDetailMap,
  )

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
      `}
      onSubmit={onSubmit}
    >
      <div
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 24px;
        `}
      >
        {translate('Verify Code and Change Password')}
      </div>
      {/* hidden email field for accessibility reasons */}
      <TextInputField
        css={css`
          display: none;
        `}
        value={props.email}
        placeholder={''}
        onChange={(): null => {
          return null
        }}
        type={'email'}
        autoComplete={'email'}
        errors={errorDetailMap?.['email']}
        inputAriaLabel={'email address'}
      />
      <CodeVerification
        value={flatFields.emailCode}
        onChange={(val: string): void => onFlatFieldChange('emailCode', val)}
        errors={flatFieldsErrors['emailCode']}
        email={props.email}
      />
      <div
        css={css`
          margin-bottom: 40px;
        `}
      >
        <PasswordInputField
          value={flatFields.newPassword}
          onChange={(val: string): void =>
            onFlatFieldChange('newPassword', val)
          }
          errors={flatFieldsErrors['newPassword']}
          fieldLabel={translate('New password')}
          isNewPassword
        />
      </div>
      <Button
        type={ButtonTypes.submit}
        design={ButtonDesigns.designCta}
        text={translate('Change Password')}
        loading={loading}
        // TODO: add validation for disabled state
        disabled={loading}
        big
      />
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
    </form>
  )
}

export default ForgotPasswordForm
